/**
 * Lab / bet-service books → Net Exposure list shape.
 *
 * `getBooksForBackend` carries horse P/L + netExposure.
 * `getBooksForBackendPre` is inventory-only (NE cells 0).
 *
 * Client reshape mirrors Lab `net-exposure.js` `insideMethod`:
 * `#` BM flags, Match Odds pull-aside, Artemis `key: "PB"` → dataPrem merge,
 * soccer PB market order.
 */

import type {
  HorseAmount,
  NetExposureEvent,
  NetExposureMarket,
  SportName,
} from '@/features/net-exposure/fixture'

export type BookMarketPre = {
  marketId: string
  eventName: string
  marketName: string
  eventType?: string
}

export type BookEventGroupPre = {
  _id: string
  data: BookMarketPre[]
  inPlay?: boolean
  /** Lab Artemis premium fan-out marker. */
  key?: 'PB'
}

/** bet-service `BookHorseAmount`. */
export type BookHorseAmount = {
  horse: string
  amount: number
  name?: string
  sortPriority?: number
}

/** bet-service `BookMarketBackend`. */
export type BookMarketBackend = {
  marketId: string
  user?: string
  horse?: BookHorseAmount[]
  netExposure: number
  totalNetExposure?: number
  eventName: string
  marketName: string
  bookmakerFlag: boolean
  matchoddsFlag: boolean
  fancyFlag: boolean
  lineLadder?: boolean
  /** Lab CM fancy/line tree — stake × page commission rate (Detail Book). */
  netexpoCommision?: number
  eventType?: string
}

export type BookEventGroupBackend = {
  _id: string
  data: BookMarketBackend[]
  inPlay?: boolean
  /** Lab Artemis premium fan-out marker (`xyz[i].key = "PB"`). */
  key?: 'PB'
}

const SPORT_SET = new Set<Exclude<SportName, 'All'>>([
  'Cricket',
  'Soccer',
  'Tennis',
  'Horse Racing',
  'Greyhound Racing',
])

export function eventTypeToSport(
  raw: string | undefined,
  fallback: Exclude<SportName, 'All'> = 'Cricket',
): Exclude<SportName, 'All'> {
  const t = (raw || '').trim()
  if (SPORT_SET.has(t as Exclude<SportName, 'All'>)) {
    return t as Exclude<SportName, 'All'>
  }
  return fallback
}

function classifyMarket(
  name: string,
  marketId?: string,
): Pick<NetExposureMarket, 'isMatchOdds' | 'bookMakerforNetExposure' | 'bookmakerFlag'> {
  // Lab net-exposure.js: marketId with `#` (and no `-`) → exchange bookmaker.
  if (marketId?.includes('#')) {
    const exchangeBm = !marketId.includes('-')
    return {
      isMatchOdds: false,
      bookMakerforNetExposure: exchangeBm,
      bookmakerFlag: false,
    }
  }

  const n = name.toLowerCase()
  const isMatchOdds = n.includes('match odds') && !n.includes('bookmaker')
  const bookmakerFlag =
    n.includes('premium') || n.includes('artemis') || /\(bookmaker\)/.test(n)
  const bookMakerforNetExposure =
    (n.includes('bookmaker') || n.endsWith('#bm')) && !bookmakerFlag
  return { isMatchOdds, bookMakerforNetExposure, bookmakerFlag }
}

/**
 * Layout flags for the Net Exposure grid.
 * API `bookmakerFlag` lumps exchange BM + premium BM — split with the name rules.
 */
export function flagsFromBackend(
  market: Pick<
    BookMarketBackend,
    'marketName' | 'bookmakerFlag' | 'matchoddsFlag' | 'fancyFlag' | 'lineLadder'
  > & { marketId?: string },
): Pick<NetExposureMarket, 'isMatchOdds' | 'bookMakerforNetExposure' | 'bookmakerFlag'> {
  if (market.fancyFlag || market.lineLadder) {
    return { isMatchOdds: false, bookMakerforNetExposure: false, bookmakerFlag: false }
  }
  // Lab prefers marketId `#` over name when classifying exchange BM.
  if (market.marketId?.includes('#')) {
    return classifyMarket(market.marketName, market.marketId)
  }
  const named = classifyMarket(market.marketName)
  if (market.bookmakerFlag) {
    if (named.bookmakerFlag) return named
    return { isMatchOdds: false, bookMakerforNetExposure: true, bookmakerFlag: false }
  }
  if (market.matchoddsFlag && named.isMatchOdds) return named
  return named
}

/** Lab `_.sortBy(horse.horse, ["sortPriority"])` then grid remap. */
export function sortHorsesByPriority(horses: BookHorseAmount[]): BookHorseAmount[] {
  if (horses.every((h) => h.sortPriority == null)) return horses
  return [...horses].sort((a, b) => (a.sortPriority ?? 99) - (b.sortPriority ?? 99))
}

/** Map API horses onto the 1 / 2 / X columns (draw is always index 2). */
export function horsesForGrid(horses: HorseAmount[]): HorseAmount[] {
  if (horses.length < 3) return horses
  const drawAt = horses.findIndex((h) => /^(x|the draw|draw)$/i.test(h.name.trim()))
  if (drawAt < 0 || drawAt === 2) return horses
  const rest = horses.filter((_, i) => i !== drawAt)
  const first = rest[0]
  const second = rest[1]
  const draw = horses[drawAt]
  if (!first || !second || !draw) return horses
  return [first, second, draw]
}

function mapHorse(h: BookHorseAmount): HorseAmount {
  const selectionId = String(h.horse ?? '').trim()
  const name = (h.name ?? '').trim() || selectionId
  return {
    name,
    amount: Number(h.amount) || 0,
    ...(selectionId ? { selectionId } : {}),
  }
}

/**
 * Lab `getSoccerPbMarketSortOrder` — null for exchange "Match Odds" (sorts as tier 2).
 * Match 1x2 → 0, HT → 1, everything else → 2.
 */
export function soccerPbMarketSortOrder(marketName: string): number | null {
  const name = (marketName || '').trim().toLowerCase()
  if (name === 'match odds') return null
  if (name === 'match 1x2') return 0
  if (name === 'match 1x2 - half time') return 1
  return 2
}

/** Lab `applySoccerPbMarketSort` on event `dataPrem`. */
export function applySoccerPbMarketSort(markets: NetExposureMarket[]): NetExposureMarket[] {
  return [...markets].sort((a, b) => {
    const oa = soccerPbMarketSortOrder(a.marketName)
    const ob = soccerPbMarketSortOrder(b.marketName)
    const ta = oa === null ? 2 : oa
    const tb = ob === null ? 2 : ob
    if (ta !== tb) return ta - tb
    return (a.marketName || '').trim().toLowerCase().localeCompare((b.marketName || '').trim().toLowerCase())
  })
}

function marketsToEvent(
  _id: string,
  markets: NetExposureMarket[],
  sport: Exclude<SportName, 'All'>,
  inPlay?: boolean,
  isPremiumGroup = false,
): NetExposureEvent {
  if (isPremiumGroup) {
    // Lab: PB groups move entire `data` (+ pulled Match Odds) into dataPrem.
    const dataPrem = markets.map((m) => ({ ...m, key: 'PB' as const }))
    const matchOddsPrem = dataPrem.find(
      (m) => m.marketName === 'Match Odds' || /match odds/i.test(m.marketName),
    )
    return { _id, sport, data: [], dataPrem, matchOddsPrem, inPlay }
  }

  const matchOdds = markets.find((m) => m.isMatchOdds)
  const dataPrem = markets.filter((m) => m.bookmakerFlag)
  const data = markets.filter((m) => !m.isMatchOdds && !m.bookmakerFlag)
  const matchOddsPrem = dataPrem.find((m) => /match odds/i.test(m.marketName))
  return { _id, sport, matchOdds, data, dataPrem, matchOddsPrem, inPlay }
}

/**
 * Lab `books.reduce` — merge exchange + Artemis PB groups that share `_id`.
 */
export function mergeLabNetExposureEvents(events: NetExposureEvent[]): NetExposureEvent[] {
  const out: NetExposureEvent[] = []
  for (const item of events) {
    const index = out.findIndex((elem) => elem._id === item._id)
    if (index === -1) {
      out.push(item)
      continue
    }
    const acc = out[index]!
    const isPb = item.dataPrem.length > 0 && item.data.length === 0 && !item.matchOdds
    if (isPb) {
      out[index] = {
        ...acc,
        dataPrem: item.dataPrem,
        matchOddsPrem: item.matchOddsPrem ?? acc.matchOddsPrem,
        inPlay: acc.inPlay ?? item.inPlay,
      }
    } else {
      out[index] = {
        ...acc,
        data: item.data,
        matchOdds: item.matchOdds ?? acc.matchOdds,
        // Keep prior PB if this row is exchange-only.
        dataPrem: acc.dataPrem.length ? acc.dataPrem : item.dataPrem,
        matchOddsPrem: acc.matchOddsPrem ?? item.matchOddsPrem,
        inPlay: item.inPlay ?? acc.inPlay,
        sport: item.sport || acc.sport,
      }
    }
  }
  return out
}

function finalizeEvents(events: NetExposureEvent[], sportHint: SportName): NetExposureEvent[] {
  const merged = mergeLabNetExposureEvents(events)
  if (sportHint !== 'Soccer') return merged
  return merged.map((event) =>
    event.dataPrem.length
      ? { ...event, dataPrem: applySoccerPbMarketSort(event.dataPrem) }
      : event,
  )
}

function fallbackSport(groupsSport: string | undefined, sportHint: SportName): Exclude<SportName, 'All'> {
  return eventTypeToSport(groupsSport, sportHint !== 'All' ? sportHint : 'Cricket')
}

/** Map Pre event groups into the Net Exposure grid model. */
export function preGroupsToEvents(
  groups: BookEventGroupPre[],
  sportHint: SportName = 'All',
): NetExposureEvent[] {
  const events = groups.map((group) => {
    const sport = fallbackSport(group.data[0]?.eventType, sportHint)
    const markets: NetExposureMarket[] = group.data.map((m) => ({
      marketId: m.marketId,
      marketName: m.marketName,
      netExposure: 0,
      horse: [],
      eventName: m.eventName,
      ...classifyMarket(m.marketName, m.marketId),
    }))
    return marketsToEvent(group._id, markets, sport, group.inPlay, group.key === 'PB')
  })
  return finalizeEvents(events, sportHint)
}

/** Map full `getBooksForBackend` groups (horse P/L + NE) into the grid model. */
export function backendGroupsToEvents(
  groups: BookEventGroupBackend[],
  sportHint: SportName = 'All',
): NetExposureEvent[] {
  const events = groups.map((group) => {
    const sport = fallbackSport(group.data[0]?.eventType, sportHint)
    const markets: NetExposureMarket[] = group.data.map((m) => ({
      marketId: m.marketId,
      marketName: m.marketName,
      netExposure: Number(m.netExposure) || 0,
      horse: horsesForGrid(sortHorsesByPriority(m.horse ?? []).map(mapHorse)),
      eventName: m.eventName,
      ...flagsFromBackend(m),
    }))
    return marketsToEvent(group._id, markets, sport, group.inPlay, group.key === 'PB')
  })
  return finalizeEvents(events, sportHint)
}

/** Lab `isBetfairStyleMarketId` — numeric BF id with a dot, no `#` BM suffix. */
export function isBetfairStyleMarketId(marketId: string | undefined): boolean {
  const id = String(marketId || '')
  return id.includes('.') && !id.includes('#')
}

/**
 * Lab `net-exposure-new.js` CM path (`showBetfairBook`):
 * Sportsbook books only — drop Artemis PB, fancy, and exchange BM rows.
 * Keep / promote Match Odds (or another BF-style multi-runner market).
 */
export function toBetfairNetExposureEvents(events: NetExposureEvent[]): NetExposureEvent[] {
  const out: NetExposureEvent[] = []
  for (const event of events) {
    let matchOdds = event.matchOdds
    if (!matchOdds) {
      // Lab setBfBookMarket — multi-runner, not BM / `#`, prefer BF-style id.
      const candidates = [...event.data, ...event.dataPrem].filter(
        (m) =>
          !m.bookMakerforNetExposure &&
          !m.bookmakerFlag &&
          (m.horse?.length ?? 0) >= 2 &&
          !String(m.marketId).includes('#'),
      )
      matchOdds =
        candidates.find((m) => isBetfairStyleMarketId(m.marketId)) ?? candidates[0]
    }
    if (!matchOdds) continue
    out.push({
      ...event,
      matchOdds,
      data: [],
      dataPrem: [],
      matchOddsPrem: undefined,
    })
  }
  return out
}
